/* ============================================================
   <sw-media-gallery> — PDP gallery controller
   ============================================================
   Desktop: thumbnails on the left swap the main image.
   Mobile:  horizontal scroll-snap carousel of media (no thumbs).

   Keeps `data-active` on the active main item, and (on desktop)
   toggles `aria-current="true"` on the matching thumbnail. Mobile
   scroll position drives `data-active` via IntersectionObserver so
   analytics / metadata stay in sync; layout itself is pure CSS.

   Named `sw-media-gallery` to avoid colliding with Dawn's
   `<media-gallery>` controller — both can coexist on the same page.
   ============================================================ */
class SwMediaGallery extends HTMLElement {
  constructor() {
    super();
    this._onThumbClick = this._onThumbClick.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onThumbsScroll = this._onThumbsScroll.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this.mainItems = Array.from(this.querySelectorAll('[data-sw-mg-main-item]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-sw-mg-thumb]'));
    this.mainList = this.querySelector('[data-sw-mg-main-list]');
    this.thumbsList = this.querySelector('.sw-mg__thumbs');
    this.thumbsWrap = this.querySelector('.sw-mg__thumbs-wrap');

    this.thumbs.forEach((btn) => btn.addEventListener('click', this._onThumbClick));

    // Mobile-only infinite loop: clone last item to start of list and first
    // item to end. After scroll settles on a clone, silently jump to the
    // matching real item. mainItems still holds only originals so the IO
    // and active-state logic continue to work unchanged.
    this._setupLoop();

    if ('IntersectionObserver' in window && this.mainList) {
      this._io = new IntersectionObserver((entries) => {
        // Desktop layout stacks items in the same grid cell via
        // opacity, so every item appears 100% intersecting to the
        // observer. Acting on it would cause the last item in source
        // order to win and steal the active state. IO is only useful
        // for mobile carousel scroll tracking.
        if (!this._isCarousel()) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            const mediaId = entry.target.dataset.mediaId;
            this.setActive(mediaId, { scrollMain: false });
          }
        });
      }, { root: this.mainList, threshold: [0.6] });
      this.mainItems.forEach((el) => this._io.observe(el));
    }

    document.addEventListener('variant:change', this._onVariantChange);

    // Thumb-column scroll shadows: dynamic top/bottom fade based on
    // scroll position so the cue points only toward un-shown thumbs.
    if (this.thumbsList && this.thumbsWrap) {
      this.thumbsList.addEventListener('scroll', this._onThumbsScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this._updateScrollShadows();
    }

    const initial = this.mainItems.find((el) => el.dataset.active === 'true') || this.mainItems[0];
    // On first paint, sync active state only — never scroll. setActive's
    // thumb scroll uses scrollIntoView, which scrolls every scrollable
    // ancestor including the window; on a landing page the gallery sits
    // mid-body, so a thumb scroll here would yank the whole page down to
    // the product on load. scrollThumb is wanted only for genuine user
    // interactions (variant change), never for initialisation.
    if (initial) this.setActive(initial.dataset.mediaId, { scrollMain: false, scrollThumb: false });

    // After initial paint, position the carousel so the active real item is
    // centered (with the lastClone peeking on the left). Without this the
    // carousel opens at scrollLeft 0 — i.e., centered on the lastClone.
    if (this._loopReady && initial) {
      requestAnimationFrame(() => this._scrollToItemCentered(initial, false));
    }
  }

  disconnectedCallback() {
    this.thumbs.forEach((btn) => btn.removeEventListener('click', this._onThumbClick));
    document.removeEventListener('variant:change', this._onVariantChange);
    if (this.thumbsList) this.thumbsList.removeEventListener('scroll', this._onThumbsScroll);
    if (this.mainList && this._onLoopScroll) this.mainList.removeEventListener('scroll', this._onLoopScroll);
    if (this.mainList && this._onLoopScrollEnd) this.mainList.removeEventListener('scrollend', this._onLoopScrollEnd);
    window.removeEventListener('resize', this._onResize);
    if (this._io) { this._io.disconnect(); this._io = null; }
  }

  _onThumbsScroll() {
    this._updateScrollShadows();
  }

  _onResize() {
    this._updateScrollShadows();
    // If the user resized down into the mobile breakpoint after initial
    // load, set up the loop now (idempotent — guarded by _loopReady).
    this._setupLoop();
  }

  _updateScrollShadows() {
    const list = this.thumbsList;
    const wrap = this.thumbsWrap;
    if (!list || !wrap) return;
    const atTop = list.scrollTop <= 0;
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    wrap.dataset.atTop = atTop ? 'true' : 'false';
    wrap.dataset.atBottom = atBottom ? 'true' : 'false';
  }

  _onThumbClick(e) {
    const btn = e.currentTarget;
    const mediaId = btn.dataset.swMgThumb;
    if (!mediaId) return;
    this.setActive(mediaId, { scrollMain: true, scrollThumb: false });
  }

  _onVariantChange(e) {
    const mediaId = e.detail && e.detail.mediaId;
    if (!mediaId) return;
    this.setActive(String(mediaId), { scrollMain: true });
  }

  setActive(mediaId, { scrollMain = false, scrollThumb = true } = {}) {
    if (!mediaId) return;
    this.mainItems.forEach((el) => {
      el.dataset.active = el.dataset.mediaId === mediaId ? 'true' : 'false';
    });
    this.thumbs.forEach((btn) => {
      const match = btn.dataset.swMgThumb === mediaId;
      btn.setAttribute('aria-current', match ? 'true' : 'false');
    });

    if (scrollMain && this.mainList) {
      const target = this.mainItems.find((el) => el.dataset.mediaId === mediaId);
      if (target && this._isCarousel()) {
        this._scrollToItemCentered(target, true);
      }
    }
    if (scrollThumb) {
      const thumb = this.thumbs.find((b) => b.dataset.swMgThumb === mediaId);
      if (thumb) thumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  _scrollToItemCentered(target, smooth) {
    if (!target || !this.mainList) return;
    const left = target.offsetLeft - (this.mainList.clientWidth - target.clientWidth) / 2;
    this.mainList.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
  }

  /* -------- Mobile infinite loop -------- */

  _setupLoop() {
    if (!this._isCarousel()) return;
    if (this._loopReady) return;
    if (!this.mainList || this.mainItems.length < 2) return;

    const list = this.mainList;
    const first = this.mainItems[0];
    const last = this.mainItems[this.mainItems.length - 1];

    // Clone last → prepend; clone first → append. Clones are inert: no
    // data-sw-mg-main-item (so they're skipped by re-queries), aria-hidden,
    // pointer-events disabled to avoid stealing taps.
    const lastClone = last.cloneNode(true);
    lastClone.removeAttribute('data-sw-mg-main-item');
    lastClone.dataset.swMgClone = 'last';
    lastClone.removeAttribute('data-active');
    lastClone.setAttribute('aria-hidden', 'true');
    lastClone.style.pointerEvents = 'none';
    list.insertBefore(lastClone, first);

    const firstClone = first.cloneNode(true);
    firstClone.removeAttribute('data-sw-mg-main-item');
    firstClone.dataset.swMgClone = 'first';
    firstClone.removeAttribute('data-active');
    firstClone.setAttribute('aria-hidden', 'true');
    firstClone.style.pointerEvents = 'none';
    list.appendChild(firstClone);

    this._lastClone = lastClone;
    this._firstClone = firstClone;
    this._loopReady = true;

    // Watch for scroll settling. Use scrollend if available, fall back to
    // a debounced scroll listener (Safari iOS doesn't ship scrollend yet
    // in older versions).
    const onSettle = () => this._loopWrapIfNeeded();
    if ('onscrollend' in window) {
      this._onLoopScrollEnd = onSettle;
      list.addEventListener('scrollend', onSettle, { passive: true });
    } else {
      this._onLoopScroll = () => {
        if (this._loopSettleTimer) clearTimeout(this._loopSettleTimer);
        this._loopSettleTimer = setTimeout(onSettle, 120);
      };
      list.addEventListener('scroll', this._onLoopScroll, { passive: true });
    }
  }

  _loopWrapIfNeeded() {
    if (!this._loopReady || !this.mainList) return;
    if (!this._isCarousel()) return;
    if (this._jumping) return;

    const list = this.mainList;
    const center = list.scrollLeft + list.clientWidth / 2;
    const tolerance = 8;

    const lastCloneCenter = this._lastClone.offsetLeft + this._lastClone.clientWidth / 2;
    const firstCloneCenter = this._firstClone.offsetLeft + this._firstClone.clientWidth / 2;

    if (Math.abs(center - lastCloneCenter) < tolerance) {
      // Centered on the prepended clone-of-last → silently jump to the
      // real last so the user can keep scrolling backward indefinitely.
      this._jumpTo(this.mainItems[this.mainItems.length - 1]);
    } else if (Math.abs(center - firstCloneCenter) < tolerance) {
      // Centered on the appended clone-of-first → silently jump to the
      // real first so the user can keep scrolling forward indefinitely.
      this._jumpTo(this.mainItems[0]);
    }
  }

  _jumpTo(realTarget) {
    if (!realTarget) return;
    const list = this.mainList;
    // Temporarily disable scroll-snap so the instant scroll doesn't fight
    // the mandatory snap and bounce back. Restore on the next frame.
    const prevSnap = list.style.scrollSnapType;
    this._jumping = true;
    list.style.scrollSnapType = 'none';
    this._scrollToItemCentered(realTarget, false);
    requestAnimationFrame(() => {
      list.style.scrollSnapType = prevSnap;
      this._jumping = false;
    });
  }

  _isCarousel() {
    return window.matchMedia('(max-width: 749px)').matches;
  }
}

if (!customElements.get('sw-media-gallery')) {
  customElements.define('sw-media-gallery', SwMediaGallery);
}
